/** Shared types for Canvas Kingdom paper.io2 core (Phase A2 Day 5).
 *  Reference: docs/STAGE_DESIGN.md §1.1 core_mechanics.
 *  Day 5: 8-directional movement support enabled.
 */

export type Direction =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'upLeft'
  | 'upRight'
  | 'downLeft'
  | 'downRight';

export interface GridPos {
  readonly x: number;
  readonly y: number;
}

export interface GridConfig {
  readonly gridSize: number;
  readonly cellPx: number;
  readonly canvasPx: number;
}

export const DIRECTION_DELTA: Record<Direction, GridPos> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
  upLeft: { x: -1, y: -1 },
  upRight: { x: 1, y: -1 },
  downLeft: { x: -1, y: 1 },
  downRight: { x: 1, y: 1 },
};

export const ALL_DIRECTIONS: readonly Direction[] = [
  'up',
  'down',
  'left',
  'right',
  'upLeft',
  'upRight',
  'downLeft',
  'downRight',
];

export const CARDINAL_DIRECTIONS: readonly Direction[] = ['up', 'down', 'left', 'right'];

/** Cardinal step = 1 cell distance, diagonal step = sqrt(2) cell distance.
 *  Used to keep visual speed constant regardless of direction.
 */
export function directionStepDistance(dir: Direction): number {
  const delta = DIRECTION_DELTA[dir];
  const isDiagonal = delta.x !== 0 && delta.y !== 0;
  return isDiagonal ? Math.SQRT2 : 1;
}

/** Convert a 2D direction vector (e.g. (1, -1)) into the named Direction.
 *  Returns null if (0, 0) or not a unit-component vector.
 */
export function vectorToDirection(dx: number, dy: number): Direction | null {
  const cdx = Math.sign(dx);
  const cdy = Math.sign(dy);
  if (cdx === 0 && cdy === 0) return null;
  for (const dir of ALL_DIRECTIONS) {
    const delta = DIRECTION_DELTA[dir];
    if (delta.x === cdx && delta.y === cdy) return dir;
  }
  return null;
}

export function gridEquals(a: GridPos, b: GridPos): boolean {
  return a.x === b.x && a.y === b.y;
}

export function gridIndex(pos: GridPos, gridSize: number): number {
  return pos.y * gridSize + pos.x;
}

export function inBounds(pos: GridPos, gridSize: number): boolean {
  return pos.x >= 0 && pos.x < gridSize && pos.y >= 0 && pos.y < gridSize;
}

export function isReverse(a: Direction, b: Direction): boolean {
  const da = DIRECTION_DELTA[a];
  const db = DIRECTION_DELTA[b];
  return da.x === -db.x && da.y === -db.y;
}
